import React from 'react'
import { CheckCircleRounded, WarningAmberRounded, UploadRounded, RestartAltRounded } from '@mui/icons-material'
import { api } from '../../function/axios'
import { ConfirmV2 } from '../ConfirmV2'
import { IconUploadDialog } from './IconUploadDialog'
import s from './SystemV2.module.css'
// a copy of espy-dashboard/public/favicon.png — the ESPY build's own tab icon, for the preview
import dashboardStandard from '../../asset/tab-icons/dashboard-standard.png'

/*
 * System → Interface → Tab icons: the browser tab icon (favicon) of the
 * dashboard and of the admin, each on its own. The calls: GET
 * /branding/editor, PUT /branding/tab-icon/:app {icon} (a data: URL),
 * DELETE /branding/tab-icon/:app (back to the icon the build ships). Both
 * apps' public/index.html read the public GET /branding on load, so a change
 * shows on the next page load — and in this admin tab at once. Every change
 * and reset is journalled in admin_audit (entityType 'tab-icon'); the History
 * card reads it back through GET /admin-dashboard/audit.
 */

type App = 'dashboard' | 'admin'
type Row = { app: App; icon: string | null; updatedBy: string | null; updatedAt: string | null }
type AuditEntry = { id: number; adminLogin?: string | null; entityTitle?: string | null; action: string; changes?: Record<string, { from: any; to: any }> | null; createdTime: string }

const MAX_ICON = 100 * 1024
const APPS: Record<App, { title: string; tab: string; sub: string }> = {
  dashboard: { title: 'Dashboard tab', tab: 'ESPY', sub: 'What users see in their browser tab' },
  admin: { title: 'Admin tab', tab: 'ESPY Admin', sub: 'This admin panel’s tab' },
}
const errText = (e: any) => {
  const m = e?.response?.data?.message
  return (Array.isArray(m) ? m.join(', ') : typeof m === 'string' ? m : e?.message) || 'Something went wrong'
}
const when = (iso?: string | null) => (iso ? new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '')

/* the admin's own tab follows a change at once; null puts back the build's icons */
function applyAdminTabIcon(icon: string | null) {
  const base = process.env.PUBLIC_URL || ''
  document.querySelectorAll<HTMLLinkElement>('link[rel="icon"], link[rel="apple-touch-icon"]').forEach((l) => {
    if (!l.dataset.standard) l.dataset.standard = l.getAttribute('href') || ''
    l.href = icon || l.dataset.standard || `${base}/favicon.png`
  })
}

export function TabIconsV2() {
  const [rows, setRows] = React.useState<Row[] | null>(null)
  const [note, setNote] = React.useState<{ kind: 'ok' | 'err'; text: string } | null>(null)
  const [busy, setBusy] = React.useState<App | null>(null)
  const [toReset, setToReset] = React.useState<App | null>(null)

  const [history, setHistory] = React.useState<AuditEntry[] | null>(null)
  const loadHistory = React.useCallback(() => {
    api({ method: 'GET', url: '/admin-dashboard/audit', params: { entityType: 'tab-icon', limit: 20 } })
      .then(({ data }: any) => setHistory(Array.isArray(data?.list) ? data.list : []))
      .catch(() => setHistory([]))
  }, [])
  React.useEffect(() => { loadHistory() }, [loadHistory])

  React.useEffect(() => {
    api({ method: 'GET', url: '/branding/editor' })
      .then(({ data }: any) => setRows(Array.isArray(data) ? data : []))
      .catch((e) => { setRows([]); setNote({ kind: 'err', text: errText(e) }) })
  }, [])

  function take(next: Row[]) {
    setRows(next)
    loadHistory()
    applyAdminTabIcon(next.find((r) => r.app === 'admin')?.icon ?? null)
  }

  const [editing, setEditing] = React.useState<App | null>(null)

  async function save(app: App, icon: string) {
    setBusy(app)
    try {
      const { data }: any = await api({ method: 'PUT', url: `/branding/tab-icon/${app}`, body: { icon } })
      take(data)
      setEditing(null)
      setNote({ kind: 'ok', text: `${APPS[app].title} icon saved — ${app === 'admin' ? 'it is on this tab already' : 'users see it on their next page load'}` })
    } catch (e) {
      setNote({ kind: 'err', text: errText(e) })
    } finally {
      setBusy(null)
    }
  }

  async function reset(app: App) {
    setBusy(app)
    try {
      const { data }: any = await api({ method: 'DELETE', url: `/branding/tab-icon/${app}` })
      take(data)
      setNote({ kind: 'ok', text: `${APPS[app].title} is back to its default icon` })
    } catch (e) {
      setNote({ kind: 'err', text: errText(e) })
    } finally {
      setBusy(null)
    }
  }

  const standard = `${process.env.PUBLIC_URL || ''}/favicon.png`
  const currentIcon = (app: App) => rows?.find((r) => r.app === app)?.icon || (app === 'admin' ? standard : dashboardStandard)

  return (
    <div className={s.section}>
      <div className={s.sectionHead}>
        <div>
          <b>Tab icons</b>
          <span>The icon in the browser tab of the dashboard and of the admin. Without one set here, each keeps the icon it ships with.</span>
        </div>
      </div>

      {note && (
        <div className={`${s.notice} ${note.kind === 'err' ? s.noticeErr : s.noticeOk}`}>
          {note.kind === 'err' ? <WarningAmberRounded sx={{ fontSize: 17 }} /> : <CheckCircleRounded sx={{ fontSize: 17 }} />}
          <span>{note.text}</span>
          <button type="button" onClick={() => setNote(null)} aria-label="Close">×</button>
        </div>
      )}

      {rows === null && <div className={s.empty}>Loading…</div>}

      {rows && rows.length > 0 && (
        <div className={s.tiGrid}>
          {rows.map((r) => {
            const meta = APPS[r.app]
            const shown = r.icon || (r.app === 'admin' ? standard : dashboardStandard)
            return (
              <div key={r.app} className={s.card}>
                <div className={s.cardHead}>
                  <b>{meta.title}</b>
                  {r.icon ? <span className={`${s.srcTag} ${s.srcDb}`}>Changed</span> : <span className={`${s.srcTag} ${s.srcEnv}`}>Standard</span>}
                </div>
                <span className={s.muted}>{meta.sub}</span>

                <div className={s.tiTabs}>
                  {(['light', 'dark'] as const).map((mode) => (
                    <div key={mode} className={`${s.tiStrip} ${mode === 'dark' ? s.tiStripDark : ''}`}>
                      <div className={s.tiTab}>
                        <img src={shown} alt="" />
                        <span>{meta.tab}</span>
                        <i>×</i>
                      </div>
                    </div>
                  ))}
                </div>
                {!r.icon && r.app === 'dashboard' && (
                  <span className={s.hint}>Standard: the ESPY build’s icon. The ClearCheck and kabat.ai builds ship their own logos instead.</span>
                )}

                <div className={s.editRow}>
                  <button type="button" className={s.ghostBtn} onClick={() => setEditing(r.app)} disabled={busy !== null}>
                    <UploadRounded sx={{ fontSize: 16 }} />{r.icon ? 'Replace icon' : 'Upload icon'}
                  </button>
                  {r.icon && (
                    <button type="button" className={s.ghostBtn} onClick={() => setToReset(r.app)} disabled={busy !== null}>
                      <RestartAltRounded sx={{ fontSize: 16 }} />Reset to default
                    </button>
                  )}
                </div>
                <span className={s.hint}>
                  PNG (square, 64×64 or larger), ICO, SVG or WebP, up to 100 KB.
                  {r.icon && r.updatedAt ? ` Changed by ${r.updatedBy || 'an admin'}, ${when(r.updatedAt)}.` : ''}
                </span>
              </div>
            )
          })}
        </div>
      )}

      {rows && rows.length > 0 && (
        <div className={s.card}>
          <div className={s.cardHead}>
            <b>History</b>
            <span className={s.muted}>Who changed or reset a tab icon, newest first</span>
          </div>
          {history === null && <span className={s.hint}>Loading…</span>}
          {history && history.length === 0 && <span className={s.hint}>No changes yet — both tabs use their default icons.</span>}
          {history && history.length > 0 && (
            <div className={s.tiHistory}>
              {history.map((h) => {
                const app = (h.entityTitle === 'admin' ? 'admin' : 'dashboard') as App
                const icon = h.changes?.icon
                return (
                  <div key={h.id} className={s.tiHistoryRow}>
                    <span className={`${s.srcTag} ${h.action === 'reset' ? s.srcEnv : s.srcDb}`}>{h.action === 'reset' ? 'Reset' : 'Changed'}</span>
                    <b>{APPS[app].title}</b>
                    <span className={s.muted}>{icon ? `${icon.from} → ${icon.to}` : ''}</span>
                    <span className={s.spacer} />
                    <span className={s.hint}>{h.adminLogin || 'an admin'} · {when(h.createdTime)}</span>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {editing && rows && (
        <IconUploadDialog
          title={`${rows.find((r) => r.app === editing)?.icon ? 'Replace' : 'Upload'} the ${APPS[editing].title.toLowerCase()} icon`}
          current={<TabPreview app={editing} src={currentIcon(editing)} />}
          currentSrc={currentIcon(editing)}
          custom={!!rows.find((r) => r.app === editing)?.icon}
          types={['png', 'x-icon', 'vnd.microsoft.icon', 'svg+xml', 'webp']}
          accept="image/png,image/x-icon,image/vnd.microsoft.icon,image/svg+xml,image/webp"
          maxBytes={MAX_ICON}
          rules="PNG, ICO, SVG or WebP · up to 100 KB · square, 64×64 or larger"
          svg
          preview={(src) => <TabPreview app={editing} src={src} />}
          busy={busy === editing}
          onSave={(icon) => void save(editing, icon)}
          onReset={() => { setToReset(editing); setEditing(null) }}
          onClose={() => setEditing(null)}
        />
      )}

      {toReset && (
        <ConfirmV2
          header={`Reset the ${APPS[toReset].title.toLowerCase()} icon to default?`}
          yesLabel="Reset"
          onYes={() => { const a = toReset; setToReset(null); void reset(a) }}
          onClose={() => setToReset(null)}
        >
          The custom icon is removed and the {toReset === 'admin' ? 'admin' : 'dashboard'} shows the icon it ships with again.
        </ConfirmV2>
      )}
    </div>
  )
}

/* the icon big, then in a browser tab, light and dark — the form's "now" and "new" */
function TabPreview({ app, src }: { app: App; src: string }) {
  return (
    <>
      <div className={s.tiBig}><img src={src} alt={`${APPS[app].title} icon`} /></div>
      <div className={s.tiTabs}>
        {(['light', 'dark'] as const).map((mode) => (
          <div key={mode} className={`${s.tiStrip} ${mode === 'dark' ? s.tiStripDark : ''}`}>
            <div className={s.tiTab}><img src={src} alt="" /><span>{APPS[app].tab}</span><i>×</i></div>
          </div>
        ))}
      </div>
    </>
  )
}
